import ImageIcon from '@mui/icons-material/Image';
import { Avatar, Box, CircularProgress, Divider, Grid, IconButton, ListItem, ListItemAvatar, ListItemText, Modal, Tooltip, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { getGridStringOperators } from '@mui/x-data-grid';

import { DataAPIContext, GET_LIST } from '../api';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import EntityExportDialog from '../components/EntityExportDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import useGridExport from '../components/useGridExport';
import images from '../components/images';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { onTourColumn, onTourLabel } from '../components/onTourColumn';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import ActivityListToolbar from './ActivityListToolbar';
import ActivityPreview from './ActivityPreview';
import { ACTIVITY_TYPES, ConfirmationDialogText, guessPriceDisplay, PRICE_DISPLAY } from './utils';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6.5rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    associatedTripsModal: {
        position: 'absolute',
        backgroundColor: 'white',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 350,
        boxShadow: 24,
        padding: '1rem',
        borderRadius: '4px',
    },
}));

const DEFAULT_COLUMN_VISIBILITY = { id: false };
const COLUMN_VIS_KEY = 'ActivityList.columnVisibilityModel';
const GROUP_TOUR_USED_BY_TYPE = 'demo_group_tour';

const normalizeUsedByResponse = (res) => {
    if (Array.isArray(res)) {
        return { items: res, total: res.length };
    }
    return { items: res?.data ?? [], total: res?.total ?? 0 };
};

const usedByTripLabel = (ref) => ref?.group_tour?.name || ref?.description || `Trip ${ref?.group_tour?.id || ref?.id}`;
const usedByTripLink = (ref) => (ref?.group_tour?.id ? `/trip/${ref.group_tour.id}` : ref?.dashboard_link || (ref?.id ? `/trip/${ref.id}` : null));

const ActivityList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const theme = useTheme();

    const { settings, hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);

    const routeParams = useParams();
    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const urlParams = new URLSearchParams(window.location.search);
    const [relatedLoading, setRelatedLoading] = React.useState(false);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const resource = 'activity';
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);

    // XML/JSON entity export (import-compatible format)
    const [activityExportOpen, setActivityExportOpen] = React.useState(false);
    const { exportDocument: exportActivityDocument, exporting: activityExporting } = useGridExport({
        resource: 'activity',
        query,
        selectedIds,
        isAllSelected: false,
    });
    const imageHeight = 48;
    const imageWidth = 48;

    const [activityId, setActivityId] = React.useState(routeParams.id);
    const cols = isSmallScreen ? (activityId ? 0 : 12) : activityId ? 8 : 12;

    const [associatedTripsAndUsers, setAssociatedTripsAndUsers] = React.useState([]);
    const [showAssociatedTripsModal, setShowAssociatedTripsModal] = React.useState(false);

    const initialColumnVisibilityModel = React.useMemo(() => {
        try {
            const saved = localStorage.getItem(COLUMN_VIS_KEY);
            if (saved) {
                return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(saved) };
            }
        } catch (e) {
            // Ignore localStorage errors
        }
        return DEFAULT_COLUMN_VISIBILITY;
    }, []);

    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(initialColumnVisibilityModel);

    const onColumnVisibilityModelChange = React.useCallback((model) => {
        setColumnVisibilityModel(model);
        try {
            localStorage.setItem(COLUMN_VIS_KEY, JSON.stringify(model));
        } catch (e) {
            // Ignore localStorage errors
        }
    }, []);

    const onPreviewLoaded = (id, data) => {
        setRelatedLoading(false);
    };

    const clearActivitySelection = React.useCallback(() => {
        setSelectedIds([]);
        setActivityId(null);
        setRelatedLoading(false);
        history.push('/activity');
    }, [history]);

    const onClosePreview = (ev) => {
        clearActivitySelection();
    };

    const onQueryChange = (query) => {
        setQuery(query);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', query);
        props.setLastURLParams(searchParams)
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    const onSelectionModelChange = (ids, selection) => {
        //After MUI5 migration, the selection model change function reacts differently for mobile and desktop
        let selectedIds = isSmallScreen ? ids : selection;
        setSelectedIds(selectedIds);
        if (selectedIds?.length === 1) {
            // setRelatedLoading(true);
            setActivityId(selectedIds[0]);
            history.push(`/activity/${selectedIds[0]}/preview`);
        } else {
            setRelatedLoading(false);
            history.push(`/activity`);
            setActivityId(null);
        }
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        const imgUrl = (row.images || []).length > 0 && row.images[0].image ? images.url(settings.images.base, row.images[0].image, `cover_${imageWidth}x${imageHeight}`) : null;
        return (
            <React.Fragment key={idx}>
                <ListItem
                    alignItems="flex-start"
                    className={classes.listItem}
                    onClick={(ev) => {
                        onSelectionModelChange([row.id], { selectionModel: [row.id] });
                    }}
                >
                    <ListItemAvatar>
                        <Avatar alt={row.name} src={imgUrl}>
                            <ImageIcon />
                        </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                        primary={row.name || row.display_name || `(activity ${row.id})`}
                        secondary={[row.preview_text, onTourLabel(row), `ID ${row.id}`].filter(Boolean).join(' · ')}
                    />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const getAssociatedTripsAndUsers = async (activityId) => {
        let has_trips_associated = false;
        setSyncActive(true);
        try {
            const res = await dataAPI(GET_LIST, `activity/${activityId}/used_by`, {
                filter: { type: GROUP_TOUR_USED_BY_TYPE },
            });
            const { items, total } = normalizeUsedByResponse(res);
            const groupTours = items.filter((ref) => ref.group_tour || ref.type_key === GROUP_TOUR_USED_BY_TYPE);
            if (total > 0 || groupTours.length > 0) {
                has_trips_associated = true;
                setAssociatedTripsAndUsers(groupTours);
            }
        } finally {
            setSyncActive(false);
        }
        return has_trips_associated;
    };

    // const onDelete = (id) => {
    //     setDeletedList(deletedList.concat(id));
    // };

    // The grid refreshes once the background delete has finished (CP-7771), not when it starts.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['activity', 'activities'], onDeleted: refreshList });
    const doDeleteActivityList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setIdsToDelete(null);
                    clearActivitySelection();
                }
            })
            .finally(() => {
                setSyncActive(false);
            });
    };

    const onDeleteActivityList = async (idlist) => {
        setAssociatedTripsAndUsers([]);

        if (idlist.length === 1) {
            let res = await getAssociatedTripsAndUsers(idlist[0]);
            if (res) {
                setShowAssociatedTripsModal(true);
                return;
            }
        }
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_activities' : 'delete_activity');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };

    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_activity':
            case 'delete_activities':
                doDeleteActivityList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    return (
        <>
            <Modal open={showAssociatedTripsModal} onClose={() => setShowAssociatedTripsModal(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className={classes.associatedTripsModal}>
                    {syncActive && (
                        <div style={{ marginTop: '1rem', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
                            <CircularProgress />
                        </div>
                    )}
                    {associatedTripsAndUsers && associatedTripsAndUsers.length > 0 && (
                        <div>
                            {' '}
                            <Typography variant="h5">This activity cannot be deleted since it is part of the following tours:</Typography>{' '}
                            <div style={{ maxHeight: '20vh', overflow: 'auto' }}>
                                {associatedTripsAndUsers.map((atu) => (
                                    <Typography key={usedByTripLink(atu) || atu.id} style={{ margin: '1rem 0' }}>
                                        {usedByTripLabel(atu)}{' '}
                                        {usedByTripLink(atu) ? (
                                            <RouterLink style={{ color: theme.palette.primary, textDecoration: 'none' }} to={usedByTripLink(atu)}>
                                                {atu?.group_tour?.id || atu?.id}
                                            </RouterLink>
                                        ) : (
                                            atu?.group_tour?.id || atu?.id
                                        )}
                                    </Typography>
                                ))}
                            </div>{' '}
                        </div>
                    )}
                </Box>
            </Modal>
            <PageMeta title="Activities" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                    {syncActive && (
                        <div style={{ marginTop: '1rem', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
                            <CircularProgress />
                        </div>
                    )}
                    {associatedTripsAndUsers && associatedTripsAndUsers.length > 0 && (
                        <div>
                            <Typography>This activity is part of the following tours:</Typography>{' '}
                            <div style={{ maxHeight: '20vh', overflow: 'auto' }}>
                                {associatedTripsAndUsers.map((atu) => (
                                    <Typography key={usedByTripLink(atu) || atu.id} style={{ margin: '1rem 0' }}>
                                        {usedByTripLabel(atu)}{' '}
                                        {usedByTripLink(atu) ? (
                                            <RouterLink style={{ color: theme.palette.primary, textDecoration: 'none' }} to={usedByTripLink(atu)}>
                                                {atu?.group_tour?.id || atu?.id}
                                            </RouterLink>
                                        ) : (
                                            atu?.group_tour?.id || atu?.id
                                        )}
                                    </Typography>
                                ))}
                            </div>{' '}
                            <Typography style={{ marginTop: '0.5rem', marginBottom: '-1.5rem' }}>Deleting this activity will remove it from these tours. Would you like to proceed?</Typography>
                        </div>
                    )}
                </ConfirmationDialog>
            ) : null}
            <FixedLinearProgress visibility={syncActive} />
            <Grid item xs={12} container direction="row" spacing={2} p={2}>
                {activityId && isSmallScreen ? null : (
                    <Grid item xs={12} sx={{ pb: '0 !important' }}>
                        <PageHeading sx={{ mb: 1.5 }}>Activities</PageHeading>
                        <ActivityListToolbar query={query} setQuery={setQuery} resource={resource} id={activityId} selectedIds={selectedIds} onQueryChange={onQueryChange} onDeleteActivity={onDeleteActivityList} onDocumentExport={hasPerm(resource, 'export') ? () => setActivityExportOpen(true) : null} isSmallScreen={isSmallScreen} />
                        <EntityExportDialog
                            open={activityExportOpen}
                            onClose={() => setActivityExportOpen(false)}
                            entity="activity"
                            exportDocument={exportActivityDocument}
                            exporting={activityExporting}
                            selectionLabel={selectedIds?.length
                                ? `${selectedIds.length} selected activities`
                                : 'all activities matching the current search'}
                        />
                    </Grid>
                )}

                {activityId && isSmallScreen ? null : (
                    <Grid item xs={cols} sx={{ pt: '0 !important' }}>
                        <RecordList
                            className={classes.recordList}
                            resource={resource}
                            disableSelectionOnClick={false}
                            sortModel={[{ field: 'name', sort: 'asc' }]}
                            selectionModel={selectedIds}
                            onSelectionModelChange={onSelectionModelChange}
                            deps={lastUpdate}
                            relatedLoading={relatedLoading}
                            query={query}
                            listItemRenderer={listItemRenderer}
                            limitHeightForMobile={'calc(100vh - 13rem)'}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                            columns={[
                                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                                { field: 'name', headerName: 'Name', filterable: false, sortable: true, flex: 0.75, valueGetter: (p) => p.row.name || p.row.display_name || `(activity ${p.row.id})` },
                                {
                                    field: 'id',
                                    headerName: 'Activity ID',
                                    width: 108,
                                    sortable: true,
                                    filterable: false,
                                    valueFormatter: (p) => (p.value != null ? String(p.value) : ''),
                                },
                                onTourColumn(),
                                // { field: 'country', headerName: 'Country', sortable: true, width: 150, valueGetter: (p) => (countryMap[p.row.country] ? countryMap[p.row.country].name : '-') },
                                { field: 'country', headerName: 'Country', sortable: true, width: 150, valueGetter: (p) => (p.row.country ? p.row.country.name : '-') },
                                {
                                    field: 'price_type',
                                    headerName: 'Price Type',
                                    sortable: true,
                                    filterable: false,
                                    width: 150,
                                    renderCell: (p) => {
                                        return <p style={{ textTransform: 'capitalize' }}>{PRICE_DISPLAY[guessPriceDisplay(p.row)]}</p>;
                                    },
                                },
                                { field: 'type', headerName: 'Type', sortable: true, width: 150, type: 'singleSelect',
                                    valueOptions: Object.keys(ACTIVITY_TYPES).map((k) => ({ value: +k, label: ACTIVITY_TYPES[k] })),
                                    valueFormatter: (p) => ACTIVITY_TYPES[p.value] || '',
                                },
                                { field: 'category', headerName: 'Category', sortable: false, width: 150, valueGetter: (p) => p.row.category?.title || '',
                                    filterOperators: getGridStringOperators().filter((op) => op.value === 'contains'),
                                },
                                { field: 'preview_text', headerName: 'Preview Text', sortable: false, flex: 1 },
                                { field: 'atag', headerName: 'Access tags', sortable: false, flex: 0.5, valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', '),
                                    filterOperators: getGridStringOperators().filter(
                                        (op) => op.value === 'contains'
                                    ),                        
                                },
                                {
                                    field: 'buttons',
                                    headerName: 'Action',
                                    sortable: false,
                                    renderCell: (p) => {
                                        return (
                                            <Grid container alignItems="center">
                                                <Grid item pt={0.75}>
                                                    <Tooltip title="Edit">
                                                        <IconButton >
                                                            <RouterLink to={`/activity/${p.id}`} onClick={(e) => e.stopPropagation()}>
                                                                <EditIcon fontSize="small" color="primary" />
                                                            </RouterLink>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item>
                                                    <DeleteIconButton
                                                        perm="activity.delete"
                                                        size="medium"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDeleteActivityList([p.row.id]);
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        );
                                    },
                                },
                            ]}
                        />
                    </Grid>
                )}
                {activityId ? (
                    <Grid item xs={12 - cols}>
                        <ActivityPreview resource={resource} id={activityId} onLoad={onPreviewLoaded} onClose={onClosePreview} setLastUpdate={setLastUpdate} handleDelete={onDeleteActivityList} />
                    </Grid>
                ) : null}
            </Grid>
        </>
    );
};

export default ActivityList;
